import React, { useContext } from "react"
import { Div, Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSliders, faTags, faCreditCard, faReceipt } from "@fortawesome/free-solid-svg-icons"
import Card from "../atoms/Card"
import Tile from "../atoms/Tile"
import { theme } from "../settings/config"
import { modalContext } from "../providers/modal.provider"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import { isOwnSetting, getSportLessonTypes } from "../utils/school.utils"
import { getCourtFeeAtDate, isCourtPaidByPlayers, isPaidToSchool } from "../utils/billing.utils"

// Quanti tipi di lezione si fanno qui: è quello che la sezione decide, e il
// numero che dice se il lavoro è stato fatto — più del numero di sport.
// Finché nessuno ha scelto valgono quelli che lo sport propone.
const countLessonTypes = (settings, sportIDs, sports, getLessonType) =>
	sportIDs.reduce(
		(total, sportID) =>
			total +
			getSportLessonTypes(settings, sportID, sports?.[sportID], getLessonType).length,
		0,
	)

// La tariffa che vale oggi, per far vedere un numero vero invece di un
// "impostato". Se gli sport sono più d'uno vince il primo: il tassello è un
// riassunto, il dettaglio sta dentro.
const feeToday = (courtsFee, sportIDs) => {
	for (const sportID of sportIDs) {
		const fee = getCourtFeeAtDate(courtsFee, sportID, {}, new Date())
		if (fee !== null) return fee
	}
	return null
}

// Le condizioni del maestro in questo circolo. Sono sue e le cambia lui,
// anche quando il circolo è di qualcun altro; dove non ha deciso niente
// eredita i valori comuni, e il tassello lo dice.
function CoachSchoolSettingsCard() {
	const Modal = useContext(modalContext)
	const { coach } = useContext(profileContext)
	const { getSelectedSchool, selectedSchool, getSchoolSettings } = useContext(schoolContext)
	const { getPaymentMethod, getLessonType, sports } = useContext(sharedDataContext)

	const school = getSelectedSchool()
	if (!school || !coach) return null

	const settings = getSchoolSettings()
	const sportIDs = [
		...new Set([
			...Object.keys(school.courts ?? {}),
			...Object.keys(settings.lessonPrices ?? {}),
			...Object.keys(settings.courtsFee ?? {}),
		]),
	]

	// un valore che non hai scelto tu arriva dal circolo: dirlo evita di
	// chiedersi perché cambiando circolo cambiano i numeri
	const hint = field =>
		isOwnSetting(coach, selectedSchool, field) ? null : "dal circolo"

	const typesCount = countLessonTypes(settings, sportIDs, sports, getLessonType)
	const methods = settings.availablePaymentMethods ?? []
	// chi paga il circolo non paga te: va detto prima dei metodi
	const paidToSchool = isPaidToSchool(settings.playersPayTo)
	const methodsHint =
		hint("availablePaymentMethods") ??
		methods
			.slice(0, 2)
			.map(methodID => getPaymentMethod(methodID)?.paymentName ?? methodID)
			.join(", ")
	const fee = feeToday(settings.courtsFee, sportIDs)
	// quando il campo lo pagano gli allievi la tariffa resta, ma non è più
	// un costo: serve a sapere quanto togliere dal prezzo della lezione
	const paidByPlayers = isCourtPaidByPlayers(settings.courtPaidBy)

	const open = section => Modal.open("schoolSection", section)

	return (
		<Card bg={theme.colors.surface}>
			<Div d="flex" align="center" m={{ b: "1rem" }} style={{ gap: "0.75rem" }}>
				<Div
					w="48px"
					h="48px"
					rounded="circle"
					bg={theme.colors.accent}
					d="flex"
					align="center"
					justify="center"
					style={{ flexShrink: 0 }}>
					<FontAwesomeIcon icon={faSliders} color="white" size="lg" />
				</Div>
				<Div>
					<Text textWeight="bold" textSize="subheader">
						Le tue condizioni
					</Text>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						a {school.schoolName}
					</Text>
				</Div>
			</Div>

			<Div d="flex" flexWrap="wrap" style={{ gap: "0.75rem" }}>
				<Tile
					icon={faTags}
					label="Tipologie"
					value={
						typesCount === 0
							? "Da impostare"
							: `${typesCount} ${typesCount === 1 ? "tipologia" : "tipologie"}`
					}
					hint={hint("lessonPrices")}
					color={theme.colors.green}
					onClick={() => open("prezzi")}
				/>
				<Tile
					icon={faCreditCard}
					label="Pagamenti"
					value={methods.length === 0 ? "Da impostare" : `${methods.length} metodi`}
					hint={paidToSchool ? "di solito al circolo" : methodsHint}
					color={theme.colors.blue}
					onClick={() => open("pagamenti")}
				/>
				<Tile
					icon={faReceipt}
					label="Costo campo"
					value={
						paidByPlayers
							? "Lo pagano loro"
							: fee === null
								? "Non lo paghi tu"
								: `${fee} €/h`
					}
					hint={
						hint("courtsFee") ??
						(paidByPlayers && fee !== null ? `${fee} €/h tolti dal prezzo` : null)
					}
					color={theme.colors.warning}
					onClick={() => open("tariffe")}
				/>
			</Div>
		</Card>
	)
}

export default CoachSchoolSettingsCard
